import type { AccountRoleTag, ChartAccount } from '@/api/models';

/**
 * Pure account write-rules for the create/edit/archive flow. Kept out of the
 * components so the field-lock, code-uniqueness, and archive-gate policies are
 * unit-testable in isolation (they are the parts most likely to regress).
 *
 * The backend is the ultimate authority on all of these — the UI only reflects
 * the rules so a user isn't offered an action the server will reject.
 */

/** The structural fields that freeze once an account has postings (§0). */
export type StructuralField = 'type' | 'normalBalance' | 'isContra';

export const STRUCTURAL_FIELDS: readonly StructuralField[] = [
    'type',
    'normalBalance',
    'isContra',
];

type PostingsSignal = {
    hasPostings: boolean;
};

/**
 * Whether an account has posted activity — the signal that locks the structural
 * fields.
 *
 * This reads the server's flag and nothing else. It used to fall back to "has a
 * non-null balance" while the feed carried no flag; that fallback is now
 * actively wrong and has been removed. Every account in the live chart has an
 * opening balance and `hasPostings: false`, so the old fallback would freeze
 * the structural fields on the entire chart.
 *
 * `hasPostings` is true only for lines in a **POSTED or REVERSED** entry whose
 * source is not `OPENING` — drafts and the opening-balance seed deliberately
 * don't count, so a mis-classified seed account stays correctable until real
 * activity lands. Today it is false everywhere because postings are not
 * integrated, which makes the lock inert. That is the expected intermediate
 * state, not a regression: the rule is implemented and tested, and it starts
 * biting the day postings arrive, with no change here.
 */
export const accountHasPostings = (account: PostingsSignal): boolean =>
    account.hasPostings;

/** The set of structural fields frozen for this account (empty if editable). */
export const frozenAccountFields = (
    account: PostingsSignal,
): Set<StructuralField> =>
    accountHasPostings(account)
        ? new Set(STRUCTURAL_FIELDS)
        : new Set<StructuralField>();

/**
 * Whether a specific structural field is locked for this account. The three
 * freeze together — the parameter is kept so call sites read as a question about
 * a field, and so a future per-field rule has somewhere to live.
 */
export const isStructuralFieldFrozen = (
    account: PostingsSignal,
    _field: StructuralField,
): boolean => accountHasPostings(account);

/**
 * Client-side, **globally-scoped** code-uniqueness check for instant feedback —
 * the server remains the final authority on save.
 *
 * OPEN ITEM: uniqueness is assumed global (not scoped to a parent/type); confirm
 * with the ledger API. `selfId` excludes the account being edited so renaming
 * its own code to itself is not a collision.
 */
export const isAccountCodeTaken = (
    code: string,
    accounts: readonly Pick<ChartAccount, 'id' | 'code'>[],
    selfId: string | null,
): boolean => {
    const wanted = code.trim();
    if (wanted === '') {
        return false;
    }
    return accounts.some(
        (account) => account.id !== selfId && account.code.trim() === wanted,
    );
};

/**
 * The account already holding `role`, if any other account does.
 *
 * Only one account may carry each non-`NONE` role. The server does **not**
 * enforce this on save — it resolves a role by taking the single match and
 * treats two as a configuration error — so a second holder saves cleanly and
 * then breaks a flow somewhere else entirely: opening balances, the year-end
 * close, or a disposal, none of which the person doing the tagging was anywhere
 * near. Catching it here is the only place it can be attributed to the edit that
 * caused it.
 *
 * Archived accounts still count. Archiving hides an account; it does not release
 * its tag, and the server's resolver does not filter them out.
 *
 * Returns the holder rather than a boolean so the message can name it — "already
 * held by 5310 Retained earnings" is actionable in a way that "already taken" is
 * not.
 */
export const accountRoleHolder = (
    role: AccountRoleTag,
    accounts: readonly Pick<
        ChartAccount,
        'id' | 'code' | 'name' | 'accountRole'
    >[],
    selfId: string | null,
): Pick<ChartAccount, 'id' | 'code' | 'name'> | null => {
    if (role === null || role === 'NONE') {
        return null;
    }
    return (
        accounts.find(
            (account) => account.id !== selfId && account.accountRole === role,
        ) ?? null
    );
};

/** Every descendant id of `rootId` (used to keep it out of its own parent picker). */
export const collectDescendantIds = (
    accounts: readonly Pick<ChartAccount, 'id' | 'parentId'>[],
    rootId: string,
): Set<string> => {
    const childrenByParent = new Map<string, string[]>();
    for (const account of accounts) {
        if (account.parentId) {
            const bucket = childrenByParent.get(account.parentId);
            if (bucket) {
                bucket.push(account.id);
            } else {
                childrenByParent.set(account.parentId, [account.id]);
            }
        }
    }
    const descendants = new Set<string>();
    const walk = (id: string) => {
        for (const childId of childrenByParent.get(id) ?? []) {
            if (!descendants.has(childId)) {
                descendants.add(childId);
                walk(childId);
            }
        }
    };
    walk(rootId);
    return descendants;
};

/**
 * Why an account can or can't be archived.
 *
 * - `has-children`: a group with at least one active (non-archived) child —
 *   archiving is assumed to **block** (not cascade); confirm with the backend.
 * - `non-zero-balance`: archive requires a zero balance; `balanceTetri` carries
 *   the remaining signed balance (integer tetri) for the disabled-reason copy.
 */
export type AccountArchiveGate =
    | { canArchive: true }
    | {
          canArchive: false;
          reason: 'has-children' | 'non-zero-balance';
          balanceTetri: number;
      };

export const accountArchiveGate = (
    account: ChartAccount,
    allAccounts: readonly ChartAccount[],
): AccountArchiveGate => {
    const hasActiveChild = allAccounts.some(
        (candidate) => candidate.parentId === account.id && !candidate.archived,
    );
    if (hasActiveChild) {
        return { canArchive: false, reason: 'has-children', balanceTetri: 0 };
    }

    const balanceTetri = account.balance?.signedBalanceTetri ?? 0;
    if (balanceTetri !== 0) {
        return {
            canArchive: false,
            reason: 'non-zero-balance',
            balanceTetri,
        };
    }

    return { canArchive: true };
};
